import { useEffect, useState } from 'react';
import { Amplify, Auth } from 'aws-amplify';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import type { ChangeEvent } from 'react';

import AuthPageWrapper from './AuthPageWrapper/AuthPageWrapper';
import classes from './Auth.module.css';
import Button from 'Components/UI_Library/FormElements/Button';
import TextInput from 'Components/UI_Library/FormElements/TextInput';
import Notification from 'Components/UI_Library/Notification/Notification';
import Validator from 'Lib/Validator';
import * as MESSAGES from 'Lib/Messages';
import GreyContainer from 'Components/UI_Library/GreyContainer';
import ShieldLogo from 'Components/UI_Library/Icons/NewIconSet/Shield.png';
import awsExports from '../../aws-exports';

const ResetPassword = () => {
    Amplify.configure(awsExports);
    const history = useNavigate();
    const [codeSent, setCodeSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [retypeNewPassword, setRetypeNewPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [notification, setNotification] = useState({
        error: false,
        message: '',
    });
    const [formErrors, setFormErrors] = useState<{
        email?: string;
        password?: string;
        verificationCode?: string;
        newPassword?: string;
        retypeNewPassword?: string;
    }>({});
    const location = useLocation();

    useEffect(() => {
        window.addEventListener('keypress', onEnter, false);
        return () => window.removeEventListener('keypress', onEnter, false);
    }, [email, newPassword, retypeNewPassword, verificationCode, codeSent]);

    const onEnter = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (codeSent) resetPassword();
            else sendCode();
        }
    };

    const sendCode = async () => {
        if (isValid()) {
            setLoading(true);

            await Auth.forgotPassword(email)
                .then(() => {
                    setCodeSent(true);
                    setLoading(false);
                })
                .catch((err) => {
                    // FIXME: This doesn't seem to work, forgotPassword doesn't throw? From what I can tell the response
                    // from server is identical whether the email is verified or not.
                    setLoading(false);
                    if (err.message) {
                        let errorMessage = err.message.replace('client id', 'Client ID');
                        if (
                            errorMessage.indexOf('Cannot reset password') > -1 &&
                            errorMessage.indexOf('registered/verified') > -1
                        ) {
                            errorMessage += `. You will be redirected to the Email Confirmation screen in a few seconds.`;
                        }

                        setNotification({ error: true, message: errorMessage });
                        if (err.message === 'Attempt limit exceeded, please try after some time.') return;
                        setTimeout(() => {
                            history(`/verify-email?re_active=1`);
                        }, 4000);
                    }
                });
        }
    };

    const resetPassword = () => {
        if (newPasswordIsValid()) {
            setLoading(true);

            Auth.forgotPasswordSubmit(email, verificationCode.trim(), newPassword)
                .then(() => {
                    setNotification({
                        error: false,
                        message: 'Password updated successfully. You will be redirected to sign in shortly',
                    });

                    setTimeout(() => {
                        history('/signin');
                    }, 4000);
                })
                .catch((err) => {
                    setLoading(false);
                    if (err.message) {
                        setNotification({ error: true, message: err.message });
                    }
                });
        }
    };

    const isValid = () => {
        setNotification({ error: false, message: '' });
        setFormErrors({});

        const errors: { email?: string } = {};
        let isError = false;
        const validator = new Validator();

        if (!validator.required(email)) {
            errors.email = MESSAGES.ERROR_REQUIRED;
            isError = true;
        } else if (!validator.email(email)) {
            errors.email = MESSAGES.ERROR_EMAIL;
            isError = true;
        }

        if (!window.navigator.onLine) {
            setNotification({ error: true, message: MESSAGES.ERROR_NETWORK_CONNECTION });
        }

        if (isError) {
            setFormErrors(errors);
            return false;
        }

        return true;
    };

    const newPasswordIsValid = () => {
        setNotification({ error: false, message: '' });
        setFormErrors({});

        const errors: { newPassword?: string; retypeNewPassword?: string; verificationCode?: string } = {};
        let isError = false;
        const validator = new Validator();

        if (!validator.required(newPassword)) {
            errors.newPassword = MESSAGES.ERROR_REQUIRED;
            isError = true;
        } else if (!validator.minLength(newPassword, 8)) {
            errors.newPassword = MESSAGES.ERROR_MINLENGTH;
            isError = true;
        }
        if (!validator.required(retypeNewPassword)) {
            errors.retypeNewPassword = MESSAGES.ERROR_REQUIRED;
            isError = true;
        } else if (!validator.match(retypeNewPassword, newPassword)) {
            errors.retypeNewPassword = MESSAGES.ERROR_CONFIRM_PASS;
            isError = true;
        }
        if (!validator.required(verificationCode.trim())) {
            errors.verificationCode = MESSAGES.ERROR_REQUIRED;
            isError = true;
        }

        if (isError) {
            setFormErrors(errors);
            return false;
        }

        return true;
    };

    const pageTitle = () => {
        if (location.pathname.includes('update-pwd')) return 'Update password';
        if (codeSent) return 'Reset password';
        return 'Forgot password';
    };

    return (
        <AuthPageWrapper>
            <div className={classes.form}>
                <h2 className={classes.title}>{pageTitle()}</h2>

                {location.pathname.includes('update-pwd') && !codeSent ? (
                    <div className={classes.updateRequired}>
                        <img src={ShieldLogo} alt="Shield" />
                        <span>
                            We have updated our password standards and a reset is required. Multi-factor authentication
                            will also be enforced soon.
                        </span>
                    </div>
                ) : null}
                <div className={classes.formInputs}>
                    {!codeSent ? (
                        <>
                            <TextInput
                                type="email"
                                name="email"
                                label="Email"
                                required
                                placeholder="Enter email address"
                                id="login-user"
                                value={email || ''}
                                autoFocus
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                errorMessage={formErrors?.email}
                            />
                            <Notification
                                isError={notification.error}
                                message={notification.message}
                                handleCloseMsg={() => setNotification({ error: false, message: '' })}
                            />
                            <Button
                                type="primary"
                                text="Send code"
                                onClick={sendCode}
                                disabled={loading}
                                loading={loading}
                            />
                        </>
                    ) : (
                        <>
                            <GreyContainer>
                                <h6>Password requirements</h6>
                                <ul>
                                    <li>At least 8 characters</li>
                                    <li>A number</li>
                                    <li>A special character</li>
                                    <li>An uppercase letter</li>
                                    <li>A lowercase letter</li>
                                </ul>
                            </GreyContainer>
                            <TextInput
                                label="Email address"
                                required
                                name="email"
                                type="email"
                                value={email}
                                errorMessage={formErrors.email}
                                disabled
                            />

                            <TextInput
                                label="New password"
                                required
                                name="password"
                                type="password"
                                value={newPassword}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                                errorMessage={formErrors.newPassword}
                            />
                            <TextInput
                                label="Retype new password"
                                required
                                name="cfpassword"
                                type="password"
                                value={retypeNewPassword}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setRetypeNewPassword(e.target.value)}
                                errorMessage={formErrors.retypeNewPassword}
                            />
                            <TextInput
                                label="Verification code"
                                required
                                name="code"
                                type="text"
                                value={verificationCode}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setVerificationCode(e.target.value)}
                                errorMessage={formErrors.verificationCode}
                            />
                            <Notification
                                isError={notification.error}
                                message={notification.message}
                                handleCloseMsg={() => setNotification({ error: false, message: '' })}
                            />
                            <Button
                                type="primary"
                                text="Reset"
                                loading={loading}
                                disabled={loading}
                                onClick={resetPassword}
                            />
                        </>
                    )}
                </div>
                <div className={classes.authLink}>
                    <p>
                        Remembered?
                        {` `}
                        <Link to="/signin">Sign in</Link>
                    </p>
                </div>
            </div>
        </AuthPageWrapper>
    );
};

export default ResetPassword;
